import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Check, ChevronDown, ChevronUp } from 'lucide-react';
import '../../../shared/StyledInput.css';
import NumberInput from '../../../shared/NumberInput';
import { ProductThumb } from '../../../shared/ProductThumb';
import { formatDate, todayInTenant } from '../../../shared/tenantClock';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';

const PRODUCT_FIELDS = [
  { key: 'weight', label: 'Weight', unit: 'weight' },
  { key: 'length', label: 'Length', unit: 'dimension' },
  { key: 'width', label: 'Width', unit: 'dimension' },
  { key: 'height', label: 'Height', unit: 'dimension' },
];
const PAGE_SIZE = 40;
const KINDS = [
  { value: 'products', label: 'Products' },
  { value: 'packaging', label: 'Packaging' },
];
const SHOW_OPTIONS = [
  { value: 'missing', label: 'Missing' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'actual', label: 'Actual' },
  { value: 'all', label: 'All' },
];
const TYPE_LABELS = { box: 'Box', mailer: 'Mailer', satchel: 'Satchel' };

// Comfortable for a fingertip: Apple's 44px minimum.
const TAP = 44;

const NO_CATEGORY = '__none__';
const HIDDEN_CATEGORIES_KEY = 'weighMeasure.hiddenCategories';
const HIDDEN_UOMS_KEY = 'weighMeasure.hiddenUoms';
const KIND_KEY = 'weighMeasure.kind';

// Per-device conveniences only: hidden categories/UoMs, Products or Packaging.
function loadStored(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
}
function saveStored(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked — the choice just lasts until the page is left.
  }
}

// A row is hidden when its category, or any parent of it, is unticked.
function isHiddenCategory(path, hidden) {
  if (!path) return hidden.has(NO_CATEGORY);
  const parts = path.split(' › ');
  return parts.some((_, i) => hidden.has(parts.slice(0, i + 1).join(' › ')));
}

const isBlank = (value) => value === null || value === undefined || value === '';
const isActual = (sources, key) => sources?.[key]?.source === 'actual';

// Missing while any field is blank, Estimate while any is still an
// Estimate, Actual once every field has been measured here.
export function statusOf(item) {
  if (item.fields.some(({ key }) => isBlank(item.values[key]))) return 'missing';
  if (item.fields.some(({ key }) => !isActual(item.sources, key))) return 'estimate';
  return 'actual';
}

// Products and Packaging in one shape: a card's fields, values and
// Estimate/Actual sources, and the URL its saves go to.
export function productItem(row) {
  return {
    id: `uom-${row.id}`,
    saveUrl: `/api/weigh-measure/${row.product_id}/${row.id}`,
    productId: row.product_id,
    title: row.sku,
    subtitle: row.name,
    tag: `${row.uom_code} — ${row.uom_name}${row.conversion_rate > 1 ? ` · ${row.conversion_rate} ${row.base_uom_code || ''}`.trimEnd() : ''}${row.is_base ? ' · Base' : ''}`,
    category: row.category,
    uomCode: row.uom_code,
    uomName: row.uom_name,
    searchText: [row.sku, row.name, row.barcode, row.uom_code].filter(Boolean).join(' ').toLowerCase(),
    fields: PRODUCT_FIELDS,
    values: { weight: row.weight, length: row.length, width: row.width, height: row.height },
    sources: row.measure_sources || {},
  };
}
function packagingItem(row) {
  return {
    id: `pkg-${row.id}`,
    saveUrl: `/api/weigh-measure/packaging/${row.id}`,
    title: row.name,
    tag: TYPE_LABELS[row.packaging_type] || row.packaging_type,
    searchText: [row.name, row.packaging_type].filter(Boolean).join(' ').toLowerCase(),
    fields: row.fields,
    values: row.values,
    sources: row.measure_sources || {},
  };
}

const CHECKBOX_STYLE = { width: 18, height: 18, margin: 0, accentColor: '#1976d2' };

// A collapsible tick-list: everything ticked shows, unticking hides it.
function HideTickPanel({ title, options, hidden, onToggle }) {
  const [open, setOpen] = useState(false);
  const hiddenCount = options.filter((opt) => hidden.has(opt.value)).length;
  return (
    <div style={{ background: '#fff', border: '1px solid #d0d5dd', borderRadius: 6, marginBottom: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minHeight: TAP, padding: '0 12px', background: 'none', border: 'none', outline: 'none', fontSize: 15, fontWeight: 600, color: '#333', fontFamily: 'inherit' }}
      >
        <span>{title}{hiddenCount ? ` · ${hiddenCount} hidden` : ''}</span>
        {open ? <ChevronUp size={18} color="#888" /> : <ChevronDown size={18} color="#888" />}
      </button>
      {open && (
        <div style={{ borderTop: '1px solid #e3e7ed', padding: '4px 0', maxHeight: 320, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#c4ccd6 transparent' }}>
          {options.map((opt) => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: TAP, padding: '0 12px', paddingLeft: 12 + (opt.depth || 0) * 22, fontSize: 15, fontWeight: opt.bold ? 600 : 400, fontStyle: opt.italic ? 'italic' : undefined, color: opt.disabled ? '#aaa' : '#333' }}>
              <input type="checkbox" checked={!hidden.has(opt.value)} disabled={opt.disabled} onChange={() => onToggle(opt.value)} style={CHECKBOX_STYLE} />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentedButtons({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', border: '1px solid #d0d5dd', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{ flex: 1, minHeight: TAP, border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, background: value === opt.value ? '#1976d2' : '#fff', color: value === opt.value ? '#fff' : '#333' }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// "Actual · 3 Mar 2026" / "Estimate · 3 Mar 2026" / "Estimate" (a figure
// from before these were kept) under a filled field.
function sourceCaption(sources, key) {
  const entry = sources?.[key];
  const label = entry?.source === 'actual' ? 'Actual' : 'Estimate';
  return entry?.on ? `${label} · ${formatDate(entry.on)}` : label;
}

const correctButtonStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  minWidth: TAP, minHeight: TAP, padding: '0 10px', borderRadius: 6,
  border: '1px solid #1976d2', background: '#fff', color: '#1976d2',
  fontFamily: 'inherit', fontSize: 14, fontWeight: 600, touchAction: 'manipulation',
};

function MeasureCard({ item, units, display, collapsed, disabled, thumb, onSave, onConfirm }) {
  const done = statusOf(item) === 'actual';
  const estimateKeys = item.fields.filter(({ key }) => !isBlank(item.values[key]) && !isActual(item.sources, key)).map(({ key }) => key);
  return (
    <div style={{ background: done ? '#f1f8f2' : '#fff', border: '1px solid ' + (done ? '#b7dfbd' : '#e3e7ed'), borderRadius: 8, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {thumb}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, overflowWrap: 'anywhere' }}>{item.title}</div>
          {!collapsed && item.subtitle && <div style={{ fontSize: 13, color: '#555', overflowWrap: 'anywhere' }}>{item.subtitle}</div>}
          <div style={{ fontSize: 13, color: '#1976d2', fontWeight: 600, marginTop: 2 }}>{item.tag}</div>
        </div>
        {done && <Check size={22} color="#2e7d32" strokeWidth={3} />}
      </div>
      {!collapsed && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
            {item.fields.map(({ key, label, unit }) => {
              const value = display[key];
              const showCorrect = !disabled && estimateKeys.includes(key);
              return (
                <div key={key}>
                  <label htmlFor={`${item.id}-${key}`} style={{ display: 'block', fontSize: 12, color: '#666', fontWeight: 600, marginBottom: 4 }}>
                    {label}{units[unit] ? ` (${units[unit]})` : ''}
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <NumberInput
                      id={`${item.id}-${key}`}
                      value={isBlank(value) ? '' : value}
                      maxDecimals={3}
                      min="0"
                      enterKeyHint="next"
                      disabled={disabled}
                      onBlur={(e) => {
                        const raw = e.target.value;
                        const val = raw === '' ? null : Number(raw);
                        const current = isBlank(value) ? null : Number(value);
                        if (val !== current) onSave({ [key]: val });
                      }}
                      style={{ flex: 1, minWidth: 0, width: '100%', boxSizing: 'border-box', minHeight: TAP, fontSize: 16, padding: '0 8px', textAlign: 'right', background: isBlank(value) ? '#fffaf0' : undefined }}
                    />
                    {showCorrect && (
                      <button type="button" title="Record this figure as an Actual" aria-label={`${label} is correct`} onClick={() => onConfirm([key])} style={correctButtonStyle}>
                        <Check size={18} strokeWidth={3} />
                      </button>
                    )}
                  </div>
                  {!isBlank(item.values[key]) && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>{sourceCaption(item.sources, key)}</div>
                  )}
                </div>
              );
            })}
          </div>
          {!disabled && estimateKeys.length > 1 && (
            <button type="button" title="Record every Estimate on this card as an Actual" onClick={() => onConfirm(estimateKeys)} style={{ ...correctButtonStyle, width: '100%', marginTop: 12 }}>
              <Check size={18} strokeWidth={3} /> All correct
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Warehouse › Weigh & Measure — a phone screen for walking the warehouse
// with a scale and tape. Every bought or sold Unit of Measure, or every
// Packaging, in SKU/name order, so it follows the shelves. A figure saved
// here is an Actual; one typed in the office (a Product's Weights &
// Dimensions page, the Packaging form) is an Estimate. ✓ records an
// Estimate that's already right as an Actual without retyping it. A card
// stays under the filter it was in when the list loaded, so finishing one
// collapses it with a tick rather than making the cards below jump.
function WeighMeasurePage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [kind, setKind] = useState(() => (loadStored(KIND_KEY, 'products') === 'packaging' ? 'packaging' : 'products'));
  const [items, setItems] = useState([]);
  const [statusAtLoad, setStatusAtLoad] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [productUnits, setProductUnits] = useState({ weight: '', dimension: '' });
  const [shipmentUnits, setShipmentUnits] = useState({ weight: '', dimension: '' });
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('missing');
  const [hidden, setHidden] = useState(() => new Set(loadStored(HIDDEN_CATEGORIES_KEY, [])));
  const [hiddenUoms, setHiddenUoms] = useState(() => new Set(loadStored(HIDDEN_UOMS_KEY, [])));
  const [searchFocused, setSearchFocused] = useState(false);
  const [shown, setShown] = useState(PAGE_SIZE);
  const [primaryImages, setPrimaryImages] = useState({});

  const load = async (which = kind) => {
    setLoading(true);
    try {
      const res = await axios.get(which === 'packaging' ? '/api/weigh-measure/packaging' : '/api/weigh-measure');
      const next = (res.data.data || []).map(which === 'packaging' ? packagingItem : productItem);
      setItems(next);
      setStatusAtLoad(Object.fromEntries(next.map((item) => [item.id, statusOf(item)])));
      setDrafts({});
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Could not load the list to weigh and measure');
    }
    setLoading(false);
  };

  useEffect(() => {
    load(kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  useEffect(() => {
    axios.get('/api/platform-settings')
      .then((res) => {
        const s = res.data.data || {};
        setProductUnits({ weight: s.weight_unit || '', dimension: s.dimension_unit || '' });
        setShipmentUnits({ weight: s.shipment_weight_unit || '', dimension: s.shipment_dimension_unit || '' });
      })
      .catch(() => {});
  }, []);

  const changeKind = (next) => {
    if (next === kind) return;
    saveStored(KIND_KEY, next);
    setItems([]);
    setShown(PAGE_SIZE);
    setKind(next);
  };

  // Every category on the list plus each of its parents, so unticking a
  // parent hides everything under it too.
  const categoryTicks = useMemo(() => {
    if (kind !== 'products') return [];
    const set = new Set();
    items.forEach((item) => {
      const parts = (item.category || '').split(' › ').filter(Boolean);
      parts.forEach((_, i) => set.add(parts.slice(0, i + 1).join(' › ')));
    });
    const ticks = [...set].sort((a, b) => a.localeCompare(b)).map((path) => {
      const parts = path.split(' › ');
      return {
        value: path,
        label: parts[parts.length - 1],
        depth: parts.length - 1,
        bold: parts.length === 1,
        disabled: parts.length > 1 && isHiddenCategory(parts.slice(0, -1).join(' › '), hidden),
      };
    });
    if (items.some((item) => !item.category)) ticks.push({ value: NO_CATEGORY, label: 'No category', italic: true });
    return ticks;
  }, [items, kind, hidden]);

  const uomTicks = useMemo(() => {
    if (kind !== 'products') return [];
    const byCode = new Map();
    items.forEach((item) => { if (!byCode.has(item.uomCode)) byCode.set(item.uomCode, item.uomName); });
    return [...byCode.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([code, name]) => ({ value: code, label: `${code} — ${name}` }));
  }, [items, kind]);

  const isItemHidden = (item) => kind === 'products' && (isHiddenCategory(item.category, hidden) || hiddenUoms.has(item.uomCode));

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      if (show !== 'all' && statusAtLoad[item.id] !== show) return false;
      if (isItemHidden(item)) return false;
      return !term || item.searchText.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, hidden, hiddenUoms, show, statusAtLoad, kind]);
  const visible = filtered.slice(0, shown);

  const toggler = (set, setter, key) => (value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
    saveStored(key, [...next]);
    setShown(PAGE_SIZE);
  };
  const anyHidden = categoryTicks.some((opt) => hidden.has(opt.value)) || uomTicks.some((opt) => hiddenUoms.has(opt.value));
  const remaining = items.filter((item) => statusOf(item) !== 'actual' && !isItemHidden(item)).length;

  const productIdsKey = kind === 'products' ? [...new Set(visible.map((item) => item.productId))].join(',') : '';
  useEffect(() => {
    if (!productIdsKey) return;
    axios.get('/api/media-images/primary-for-products', { params: { product_ids: productIdsKey } })
      .then((res) => setPrimaryImages((prev) => ({ ...prev, ...(res.data.data || {}) })))
      .catch(() => {});
  }, [productIdsKey]);

  // Saves a card's changed fields as Actuals. A Packaging's sizes go
  // together (all three inside dimensions or none), so a figure the rules
  // turn down stays typed in as a draft and goes again with the next one.
  const save = async (item, patch) => {
    const body = { ...(drafts[item.id] || {}), ...patch };
    setDrafts((d) => ({ ...d, [item.id]: body }));
    try {
      await axios.put(item.saveUrl, body);
      const on = todayInTenant();
      setItems((list) => list.map((it) => {
        if (it.id !== item.id) return it;
        const sources = { ...it.sources };
        Object.entries(body).forEach(([key, value]) => {
          if (isBlank(value)) delete sources[key];
          else sources[key] = { source: 'actual', on };
        });
        return { ...it, values: { ...it.values, ...body }, sources };
      }));
      setDrafts((d) => {
        const next = { ...d };
        delete next[item.id];
        return next;
      });
      dismissError();
    } catch (err) {
      showError(err, 'Could not save');
    }
  };
  const confirm = (item, keys) => save(item, Object.fromEntries(keys.map((key) => [key, item.values[key]])));

  const units = kind === 'packaging' ? shipmentUnits : productUnits;
  const unitsNotSet = !units.weight || !units.dimension;
  const emptyMessage = show === 'missing' && !search && !anyHidden
    ? `Every ${kind === 'packaging' ? 'Packaging' : 'product'} has its Weight and Dimensions.`
    : 'Nothing to show for these filters.';

  return (
    <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: '12px 12px 32px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, margin: '4px 0 4px' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Weigh &amp; Measure</h2>
        {!loading && !loadError && (
          <span style={{ fontSize: 14, color: remaining ? '#8a5a00' : '#2e7d32', fontWeight: 600 }}>
            {remaining ? `${remaining} to do` : 'All done'}
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
        Figures saved here are recorded as Actuals. Figures typed in the office are Estimates.
      </div>

      <SegmentedButtons options={KINDS} value={kind} onChange={changeKind} />

      {unitsNotSet && !loading && (
        <div style={{ fontSize: 13, color: '#8a5a00', background: '#fff8e1', border: '1px solid #f0d998', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
          {kind === 'packaging'
            ? 'Set the Shipment Weight Unit and Shipment Dimension Unit under Platform Settings before entering values here.'
            : 'Set Product UoM Weight Unit and Product UoM Dimension Unit under Platform Settings before entering values here.'}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 6, padding: '0 10px', marginBottom: 12,
        border: '1px solid ' + (searchFocused ? '#1976d2' : '#d0d5dd'),
        boxShadow: searchFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
      }}
      >
        <Search size={16} color="#888" />
        <input
          type="search"
          placeholder={kind === 'packaging' ? 'Packaging name' : 'SKU, name or barcode'}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShown(PAGE_SIZE); }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          style={{ flex: 1, minWidth: 0, minHeight: TAP, border: 'none', outline: 'none', fontSize: 16, padding: 0, background: 'transparent' }}
        />
      </div>

      <SegmentedButtons options={SHOW_OPTIONS} value={show} onChange={(v) => { setShow(v); setShown(PAGE_SIZE); }} />

      {kind === 'products' && (
        <>
          <HideTickPanel title="Categories" options={categoryTicks} hidden={hidden} onToggle={toggler(hidden, setHidden, HIDDEN_CATEGORIES_KEY)} />
          <HideTickPanel title="Units of Measure" options={uomTicks} hidden={hiddenUoms} onToggle={toggler(hiddenUoms, setHiddenUoms, HIDDEN_UOMS_KEY)} />
        </>
      )}

      {loading && <div style={{ padding: 16, color: '#666' }}>Loading...</div>}
      {loadError && <div style={{ padding: 16, color: 'red' }}>{loadError}</div>}
      {!loading && !loadError && !filtered.length && (
        <div style={{ padding: 16, color: '#666', textAlign: 'center' }}>{emptyMessage}</div>
      )}

      {!loading && visible.map((item) => {
        const collapsed = statusOf(item) === 'actual' && (show === 'missing' || show === 'estimate');
        return (
          <MeasureCard
            key={item.id}
            item={item}
            units={units}
            display={{ ...item.values, ...(drafts[item.id] || {}) }}
            collapsed={collapsed}
            disabled={unitsNotSet}
            thumb={kind === 'products' ? <ProductThumb entry={primaryImages[String(item.productId)]} size={collapsed ? 32 : 48} /> : null}
            onSave={(patch) => save(item, patch)}
            onConfirm={(keys) => confirm(item, keys)}
          />
        );
      })}

      {filtered.length > shown && (
        <button
          type="button"
          onClick={() => setShown((n) => n + PAGE_SIZE)}
          style={{ width: '100%', minHeight: TAP, background: '#fff', border: '1px solid #d0d5dd', borderRadius: 6, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: '#1976d2' }}
        >
          Show more ({filtered.length - shown} left)
        </button>
      )}

      {!loading && !loadError && items.length > 0 && (
        <button
          type="button"
          onClick={() => load()}
          style={{ width: '100%', minHeight: TAP, marginTop: 10, background: 'none', border: 'none', fontFamily: 'inherit', fontSize: 14, color: '#1976d2', textDecoration: 'underline' }}
        >
          Reload list
        </button>
      )}
    </div>
  );
}

export default WeighMeasurePage;
